import { useEffect, useState } from "react";
import { FormActions } from "../../components/common/FormActions";
import FormFields from "../../components/common/FormFields";
import { IFormSection, TFormHookParams } from "../../types/common";
import {
  FORM_DATA,
  RECONCILAITION,
  RECONCILAITION_DOC_TYPE,
} from "../../utils/constants";
import {
  addSelectItem,
  createColumn,
  createEditAction,
  createViewAction,
  getForm,
  getRegister,
  formatDateTime,
  NoWrap,
  objectWithUnsetKeys,
  beautify,
} from "../../utils/helpers";
import { URL_RECONCILIATIONS } from "../../utils/urls";
import useGet from "../common/useGet";
import { API_RECONCILIATIONS } from "../../utils/apis";

// Partner code options
const PARTNER_OPTIONS = [
  addSelectItem({ label: "MTN", value: "MTN" }),
  addSelectItem({ label: "AIRTEL", value: "AIRTEL" }),
  addSelectItem({ label: "CASH", value: "CASH" }),
];

// Map partner_code to filePath
const FILE_PATH_MAP: Record<
  string,
  { filePath: string; fileName: string; fileText: string }
> = {
  MTN: {
    filePath: "/media/airtel_mtn_statement.csv",
    fileName: "mtn_statement.csv",
    fileText: "Download MTN Statement CSV Template",
  },
  AIRTEL: {
    filePath: "/media/airtel_mtn_statement.csv",
    fileName: "airtel_statement.csv",
    fileText: "Download Airtel Statement CSV Template ",
  },
  CASH: {
    filePath: "/media/cash_statement.csv",
    fileName: "cash_statement.csv",
    fileText: "Download CASH Statement CSV Template ",
  },
};

const getBaseSections = (
  isEdit: boolean,
  partnerCode?: string,
): IFormSection[] => {
  const fileProps = partnerCode
    ? FILE_PATH_MAP[partnerCode] || FILE_PATH_MAP["CASH"]
    : FILE_PATH_MAP["CASH"];

  return [
    {
      title: "Partner Information",
      fields: [
        {
          field_name: "partner_name",
          label: "Partner Name",
          type: "text",
          props: {
            required: true,
          },
        },
        {
          field_name: "partner_code",
          label: "Partner Code",
          type: "select",
          props: {
            required: true,
            placeholder: "Select partner code",
          },
          options_data: PARTNER_OPTIONS,
        },
        {
          field_name: "account_number",
          label: "Account Number",
          type: "text",
          props: {
            required: false,
            placeholder: "Enter account number (optional)",
          },
        },
      ],
    },
    {
      title: "Reconciliation Period",
      fields: [
        {
          field_name: "start_date",
          label: "Start Date",
          type: "date",
          props: {
            required: true,
          },
        },
        {
          field_name: "end_date",
          label: "End Date",
          type: "date",
          props: {
            required: true,
          },
        },
      ],
    },
    {
      title: "Statement File",
      fields: [
        {
          field_name: "statement_file",
          label: "Reconciliation Statement File",
          type: "file",
          props: {
            required: !isEdit,
            placeholder: "Upload statement file",
            document_type: RECONCILAITION_DOC_TYPE,
            accept: ".csv,text/csv,application/csv",
            isDownload: true,
            downloadTemplateProps: {
              filePath: fileProps.filePath,
              fileName: fileProps.fileName,
              fileText: fileProps.fileText,
              fileTitle: fileProps.fileText,
            },
          },
        },
      ],
    },
  ];
};

export const useReconciliation = () => {
  const tableColumns = () => [
    createColumn("partner_name", true, "Partner"),
    createColumn("partner_code", true, "Partner Code"),
    createColumn("account_number", true, "Account Number"),
    createColumn("start_date", true, "Start Date"),
    createColumn("end_date", true, "End Date"),
    createColumn("status", true, "Status"),
    createColumn("total_transactions", true, "Total Transactions"),
    createColumn("record_date", true, "Record Date"),
    createColumn("actions", false),
  ];

  const tableData = (data: any) => {
    if (!data) return [];

    return data.map((item: any) => {
      return {
        ...item,
        partner_name: (
          <div className="flex flex-col">
            <span className="font-medium text-gray-900">
              {item?.partner_name || "N/A"}
            </span>
          </div>
        ),
        partner_code: (
          <NoWrap className="font-mono text-xs">
            {item?.partner_code || "N/A"}
          </NoWrap>
        ),
        account_number: (
          <NoWrap className="font-mono text-sm">
            {item?.account_number || "N/A"}
          </NoWrap>
        ),
        start_date: <NoWrap>{item?.start_date || "N/A"}</NoWrap>,
        end_date: <NoWrap>{item?.end_date || "N/A"}</NoWrap>,
        status: beautify(item?.status),
        total_transactions: (
          <span className="font-medium text-blue-600">
            {item?.total_transactions || 0}
          </span>
        ),
        record_date: (
          <NoWrap className="text-sm">
            {item?.record_date ? formatDateTime(item.record_date) : "N/A"}
          </NoWrap>
        ),
        statement_file: item?.statement_file ? (
          <a
            href={item.statement_file}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 text-sm"
          >
            View File
          </a>
        ) : (
          <span className="text-gray-400 text-sm">No File</span>
        ),
        result_summary: item?.result ? (
          <div className="max-w-xs">
            <span className="text-sm text-gray-700 truncate block">
              {item.result}
            </span>
          </div>
        ) : null,
        actions: (
          <FormActions
            actions={[
              createViewAction(URL_RECONCILIATIONS, item?.id),
              createEditAction(URL_RECONCILIATIONS, item?.id),
            ]}
          />
        ),
      };
    });
  };

  const viewData = (item: any) => {
    if (!item) {
      return {
        sections: [
          {
            title: "Error",
            fields: {
              message: "No reconciliation data available",
            },
          },
        ],
      };
    }

    return {
      sections: [
        {
          title: "Reconciliation Details",
          fields: {
            partner_name: item?.partner_name || "N/A",
            partner_code: item?.partner_code || "N/A",
            account_number: item?.account_number || "N/A",
            reconciliation_period: `${item?.start_date || "N/A"} to ${item?.end_date || "N/A"}`,
            status: item?.status || "UNKNOWN",
            total_transactions: item?.total_transactions || 0,
          },
        },
        {
          title: "Processing Information",
          fields: {
            record_date: item?.record_date
              ? formatDateTime(item.record_date)
              : "N/A",
            result: item?.result || "No result available",
            statement_file: item?.statement_file
              ? "File attached"
              : "No file attached",
            uploaded_by: item?.uploaded_by
              ? `${item.uploaded_by.first_name} ${item.uploaded_by.last_name}`.trim()
              : "System",
          },
        },
        {
          title: "System Information",
          fields: {
            record_id: item?.id || "N/A",
            created_at: item?.record_date
              ? formatDateTime(item.record_date)
              : "N/A",
            last_updated: item?.updated_at
              ? formatDateTime(item.updated_at)
              : "Same as created",
          },
        },
      ],
    };
  };

  const formRoute = getForm(URL_RECONCILIATIONS);
  const activityFilter = "reconcilations";

  return {
    tableData,
    tableColumns,
    viewData,
    formRoute,
    activityFilter,
  };
};

export function useReconciliationForm({
  id,
  form,
  updateFormValues,
}: TFormHookParams = {}) {
  const { loadData, loading, data } = useGet();
  const [sections, setSections] = useState<IFormSection[]>([]);

  useEffect(() => {
    if (updateFormValues) {
      updateFormValues({
        street_type: RECONCILAITION,
      });
    }
  }, [updateFormValues]);

  useEffect(() => {
    if (id) {
      loadData({ api: `${API_RECONCILIATIONS}/${id}` });
    }
  }, [id]);

  useEffect(() => {
    if (updateFormValues && data) {
      updateFormValues({
        partner_name: data?.partner_name || "",
        partner_code: data?.partner_code || "",
        account_number: data?.account_number || "",
        start_date: data?.start_date || "",
        end_date: data?.end_date || "",
      });
    }
  }, [updateFormValues, data]);

  const isEdit = !!id;

  useEffect(() => {
    setSections(getBaseSections(isEdit, form?.partner_code));
  }, [isEdit, form?.partner_code]);

  // Submission handler
  function onSubmit(payload: any, id?: any) {
    const params = {
      ...payload,
      statement_file:
        Array.isArray(payload?.statement_file?.file) &&
        payload?.statement_file?.file.length > 0
          ? payload?.statement_file?.file[0]
          : [],
      ...objectWithUnsetKeys(payload, ["statement_file"]),
    };

    if (id) {
      // Update existing reconciliation
      return {
        id,
        api: API_RECONCILIATIONS,
        params: params,
        type: FORM_DATA,
      };
    } else {
      // Create new reconciliation
      return {
        api: API_RECONCILIATIONS,
        params: params,
        type: FORM_DATA,
      };
    }
  }

  const title = isEdit ? "Edit Reconciliation" : "New Reconciliation";
  const redirect = getRegister(URL_RECONCILIATIONS);
  const enableSubmit = Object.keys(form || {}).length > 0;

  return {
    sections,
    title,
    redirect,
    loading,
    data,
    onSubmit,
    enableSubmit,
  };
}

export const ReconciliationForm = ({
  form,
  error,
  updateFormValue,
  onChange,
}: any) => {
  const { sections } = useReconciliationForm({
    form,
    updateFormValues: updateFormValue,
  });

  return (
    <div>
      {sections.map((section: IFormSection, index: number) => (
        <div key={index} className="mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {section.title}
          </h3>

          <FormFields
            form={form}
            error={error}
            updateFormValue={updateFormValue}
            onChange={onChange}
            fields={section.fields}
          />
        </div>
      ))}
    </div>
  );
};
